// Timed instances end `_YYYYMMDDTHHMMSSZ`; ALL-DAY instances end `_YYYYMMDD`
// (no time part) — both are series-instance shapes Google actually emits.
const GOOGLE_INSTANCE_SUFFIX = /_\d{8}(T\d{6}Z)?$/

const googleSeriesId = (googleEventId) =>
  googleEventId && GOOGLE_INSTANCE_SUFFIX.test(googleEventId)
    ? googleEventId.replace(GOOGLE_INSTANCE_SUFFIX, '')
    : null

const previousDate = (date) => {
  const [year, month, day] = String(date || '')
    .split('-')
    .map(Number)
  if (!year || !month || !day) return null
  return new Date(Date.UTC(year, month - 1, day - 1)).toISOString().slice(0, 10)
}

// The recurring dialog's own layer id: its buttons close THIS layer by id,
// never by popping whatever layer is on top.
const DELETE_LAYER_ID = 'cal-event-delete'

// A failed write puts the optimistic change back (CRUD + confirmation
// standard, rule 5). The write itself already told the person why: saveEvent
// and deleteEvent raise their own error toast. Both helpers read the LIVE
// rows, so a sync that landed meanwhile is never reverted.
const restoreRows = (root, rows) => {
  if (!rows.length || typeof root?.update !== 'function') return
  const live = root.events || []
  const present = new Set(live.map((item) => item.id))
  const missing = rows.filter((row) => !present.has(row.id))
  if (missing.length) root.update({ events: [...live, ...missing] })
}

const restoreRow = (root, row) => {
  if (typeof root?.update !== 'function') return
  const live = root.events || []
  if (!live.some((item) => item.id === row.id)) return
  root.update({ events: live.map((item) => (item.id === row.id ? row : item)) })
}

// Soft-delete each row; a row whose write fails comes back.
const deleteOrRestore = (caller, root, rows) =>
  Promise.all(
    rows.map((row) =>
      Promise.resolve(caller.call('deleteEvent', row.id)).then(
        (ok) => (ok === false ? row : null),
        () => row
      )
    )
  ).then((failed) => restoreRows(root, failed.filter(Boolean)))

// Save the changed series row; when the write fails the row before the
// change comes back.
const saveOrRestore = (caller, root, next, previous) =>
  Promise.resolve(caller.call('saveEvent', next, { falseOnFail: true })).then(
    (ok) => {
      if (ok === false) restoreRow(root, previous)
    },
    () => restoreRow(root, previous)
  )

// The delete of a calendar event — asked first, always. One occurrence of a
// recurring event gets the three-choice dialog (only this one / this and the
// later ones / Cancel); every other delete gets the house confirm
// (`confirmDialog`). Nothing is written before the person picks a delete.
export const confirmEventDeletion = (el, root, event, closeFunction, options) => {
  if (!event?.id) return
  // Open-time snapshot — used ONLY for dialog-shape decisions below. The
  // delete handlers re-read root.events at confirm time (deleteEvents), so
  // a dialog left open across a sync or another view's edit never reverts
  // what landed in the meantime.
  const events = root.events || []
  const seriesId = googleSeriesId(event.googleEventId)
  const seriesEvents = seriesId
    ? events.filter((item) => googleSeriesId(item.googleEventId) === seriesId)
    : []
  // A series-shaped instance id is proof of recurrence on its own — the
  // loaded window may hold a single materialized row of a Google series,
  // and the sync writes no per-row `recurring` metadata.
  const isRecurring =
    (event.recurring && event.recurring !== 'none') || Boolean(seriesId) || seriesEvents.length > 1
  // scope:'series' — the caller acts on the SERIES itself (the edit modal
  // edits the source row and has no occurrence context), so the dialog is
  // the plain confirm and the handler deletes the row outright instead of
  // excluding or truncating at an occurrence date.
  const seriesScope = options?.scope === 'series'
  const scopedRecurring = isRecurring && !seriesScope
  // ONE string for the modal title and the visible heading — the windows
  // layer points aria-labelledby at the title heading (modal-title-<id>),
  // so a title that differs from what the user sees makes the dialog
  // announce something the screen never shows (label-in-name mismatch,
  // WS-CAL-DELETE-DIALOG-A11Y-1). A function replacement: the title is the
  // person's own text, and `$&` in it is not a pattern.
  const eventTitle = event.title || el.call('polyglot', 'calendar.delete.eventFallback')
  const title = el
    .call(
      'polyglot',
      scopedRecurring ? 'calendar.delete.recurringTitle' : 'calendar.delete.confirmTitle'
    )
    .replace('{title}', () => eventTitle)

  // Runs only after the person picked a delete. `caller` is the element whose
  // `call` reaches the write functions.
  const deleteEvents = (caller, includeFuture = false) => {
    // Fresh read — never the open-time snapshot (see note above).
    const liveEvents = root.events || []
    const sourceEvent = liveEvents.find((item) => item.id === event.id) || event
    const isLocalRecurrence = !seriesId && sourceEvent.recurring && sourceEvent.recurring !== 'none'
    if (!seriesScope && !includeFuture && isLocalRecurrence && event.date) {
      const recurrenceExcludedDates = [
        ...new Set([...(sourceEvent.recurrenceExcludedDates || []), event.date])
      ].sort()
      const updatedEvent = { ...sourceEvent, recurrenceExcludedDates }
      root.update({
        events: liveEvents.map((item) => (item.id === sourceEvent.id ? updatedEvent : item))
      })
      saveOrRestore(caller, root, updatedEvent, sourceEvent)
      if (closeFunction) caller.call(closeFunction)
      return
    }

    if (!seriesScope && includeFuture && !seriesId && event.date > sourceEvent.date) {
      const recurrenceEndDate = previousDate(event.date)
      if (recurrenceEndDate) {
        const truncatedEvent = {
          ...sourceEvent,
          recurrenceEndType: 'on',
          recurrenceEndDate
        }
        root.update({
          events: liveEvents.map((item) => (item.id === sourceEvent.id ? truncatedEvent : item))
        })
        saveOrRestore(caller, root, truncatedEvent, sourceEvent)
        if (closeFunction) caller.call(closeFunction)
        return
      }
    }

    const liveSeriesEvents = seriesId
      ? liveEvents.filter((item) => googleSeriesId(item.googleEventId) === seriesId)
      : []
    const targets =
      includeFuture && liveSeriesEvents.length
        ? liveSeriesEvents.filter((item) => item.date >= event.date)
        : [sourceEvent]
    const targetIds = new Set(targets.map((item) => item.id))
    root.update({ events: liveEvents.filter((item) => !targetIds.has(item.id)) })
    // Google series, future scope: ONE server-side filtered soft-delete over
    // the PERSISTED series — instances outside the loaded window included
    // (CORE-CALENDAR-SERIES-BULK-SOFT-DELETE-1; the dedicated deleteFuture
    // op exists because the update op's argMaps.idPayload drops every filter
    // param, which is how the original bulk attempt died inside the SDK).
    // Feature-detected: a registry sdk that predates the op rejects with an
    // unsupported-op error and the loaded-window per-id loop takes over, so
    // this call site stays safe on sdk 3.14.785. On bulk success the sync
    // pass is kicked so the server-side tombstones reach Google without
    // waiting for the next scheduled pass.
    if (includeFuture && seriesId && typeof caller.context?.methods?.sdk?.execute === 'function') {
      caller.context.methods.sdk
        .execute('workspaceProject.calendar', 'deleteFuture', {
          seriesId,
          fromDate: event.date
        })
        .then(() => caller.call('triggerCalendarSync'))
        .catch(() => deleteOrRestore(caller, root, targets))
    } else {
      deleteOrRestore(caller, root, targets)
    }
    if (closeFunction) caller.call(closeFunction)
  }

  if (!scopedRecurring) {
    // The house confirm (standard D1). Its Confirm button calls `onConfirm`
    // and closes its own layer; `el` is the Delete button that asked.
    el.call('confirmDialog', {
      title,
      message: el.call(
        'polyglot',
        seriesScope && isRecurring ? 'calendar.delete.bodySeries' : 'calendar.delete.bodySingle'
      ),
      confirmText: el.call('polyglot', 'calendar.delete.confirm'),
      cancelText: el.call('polyglot', 'common.action.cancel'),
      confirmTheme: 'primary',
      icon: null,
      onConfirm: () => deleteEvents(el)
    })
    return
  }

  // The three-choice dialog for one occurrence of a recurring event. Its
  // buttons follow the house confirm: Cancel is the design system's
  // CancelButton, each delete is a primary Button that names its scope.
  const pick = (includeFuture) => (e, button) => {
    deleteEvents(button, includeFuture)
    button.call('closeModal', DELETE_LAYER_ID)
  }

  el.call('openModal', {
    id: DELETE_LAYER_ID,
    template: 'custom',
    // MODAL-X-OVERFLOW-1: every custom-template layer needs an outer
    // ceiling, or a short viewport pushes the Close X off-screen (the
    // class-level sweep in workspace/__tests__/modalXOverflowSweep.test.js
    // enforces this on every raw openModal({template:'custom'}) site).
    maxHeight: '90vh',
    title,
    content: {
      flow: 'y',
      gap: 'B',
      minWidth: '0',
      overflowX: 'hidden',
      Hgroup: {
        minWidth: '0',
        H: {
          tag: 'h5',
          fontSize: 'B',
          // Always the modal title — accessible name === visible heading.
          text: title,
          overflowWrap: 'anywhere'
        },
        P: {
          text: el.call('polyglot', 'calendar.delete.bodyRecurring'),
          color: 'inactive',
          overflowWrap: 'anywhere'
        }
      },
      ModalFooter: {
        flow: 'y',
        gap: 'Z1',
        // Cancel comes FIRST in DOM: the windows host focuses the top
        // layer's first focusable after paint (ModalRoot onUpdate, no
        // caller-side focus option), and a dialog must never hand a
        // reflexive Enter to a destructive action. `order` keeps the
        // VISUAL stack unchanged (delete-this, delete-future, cancel);
        // the one-step focus/visual divergence is the deliberate trade
        // for a safe initial default (WS-CAL-DELETE-DIALOG-A11Y-1).
        CancelBtn: {
          extends: 'CancelButton',
          width: '100%',
          order: '3',
          text: el.call('polyglot', 'common.action.cancel'),
          onClick: (e, button) => button.call('closeModal', DELETE_LAYER_ID)
        },
        DeleteThisBtn: {
          extends: 'Button',
          theme: 'primary',
          width: '100%',
          order: '1',
          text: el.call('polyglot', 'calendar.delete.onlyThis'),
          onClick: pick(false)
        },
        DeleteFutureBtn: {
          extends: 'Button',
          theme: 'primary',
          width: '100%',
          order: '2',
          text: el.call('polyglot', 'calendar.delete.allFuture'),
          onClick: pick(true)
        }
      }
    }
  })
}
